import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  type InputSignal,
  type OutputEmitterRef,
  type Signal,
} from '@angular/core';
import {
  UiActionMenuComponent,
  UiButtonComponent,
  UiCardComponent,
  UiLibraryIconComponent,
  type UiActionMenuItem,
} from '@nexus/frontend-shared-ui';
import type { WishlistDetailActionView } from '../../types/wishlist-detail-action-view.interface';
import type { WishlistDetailItemActionEvent } from '../../types/wishlist-detail-item-action-event.interface';
import type { WishlistDetailItemRowView } from '../../types/wishlist-detail-item-row-view.interface';
import { WishlistStatusPillComponent } from '../wishlist-status-pill/wishlist-status-pill.component';

@Component({
  selector: 'wishlist-item-row',
  imports: [
    UiActionMenuComponent,
    UiButtonComponent,
    UiCardComponent,
    UiLibraryIconComponent,
    WishlistStatusPillComponent,
  ],
  templateUrl: './wishlist-item-row.component.html',
  styleUrl: './wishlist-item-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WishlistItemRowComponent {
  public readonly row: InputSignal<WishlistDetailItemRowView> = input.required<WishlistDetailItemRowView>();
  public readonly reserveSelected: OutputEmitterRef<string> = output<string>();
  public readonly actionSelected: OutputEmitterRef<WishlistDetailItemActionEvent> =
    output<WishlistDetailItemActionEvent>();

  protected readonly hasDescription: Signal<boolean> = computed((): boolean => this.row().description !== null);
  protected readonly hasPrice: Signal<boolean> = computed((): boolean => this.row().priceLabel !== null);
  protected readonly hasShopUrl: Signal<boolean> = computed((): boolean => this.row().shopUrl !== null);
  protected readonly showShopLink: Signal<boolean> = computed(
    (): boolean => this.hasShopUrl() && (this.row().mode !== 'guest' || this.row().canReserve),
  );
  protected readonly hasPriority: Signal<boolean> = computed((): boolean => this.row().priorityLabel !== null);
  protected readonly hasReservationLines: Signal<boolean> = computed(
    (): boolean => this.row().reservationLines.length > 0,
  );
  protected readonly hasReservationEmail: Signal<boolean> = computed(
    (): boolean => this.row().reservationEmail !== null,
  );
  protected readonly hasActions: Signal<boolean> = computed((): boolean => this.row().actions.length > 0);
  protected readonly isReservationWarning: Signal<boolean> = computed(
    (): boolean => this.row().reservationTone === 'warning',
  );
  protected readonly isGuestRow: Signal<boolean> = computed((): boolean => this.row().mode === 'guest');
  protected readonly actionMenuItems: Signal<readonly UiActionMenuItem[]> = computed(
    (): readonly UiActionMenuItem[] =>
      this.row().actions.map(
        (action: WishlistDetailActionView): UiActionMenuItem => ({
          id: action.kind,
          label: action.label,
          tone: action.tone === 'danger' ? 'danger' : 'neutral',
        }),
      ),
  );
  protected readonly statusAccessibleLabel: Signal<string> = computed(
    (): string => `Status prezentu ${this.row().title}: ${this.row().statusLabel}`,
  );
  protected readonly shopLinkLabel: Signal<string> = computed(
    (): string => `Zobacz ${this.row().title} w sklepie`,
  );
  protected readonly rowAccessibleLabel: Signal<string> = computed(
    (): string => `${this.row().title}, status: ${this.row().statusLabel}`,
  );

  protected handleReserveSelected(): void {
    this.reserveSelected.emit(this.row().id);
  }

  protected handleActionIdSelected(actionKind: string): void {
    const action: WishlistDetailActionView | undefined = this.row().actions.find(
      (candidate: WishlistDetailActionView): boolean => candidate.kind === actionKind,
    );

    if (!action) {
      return;
    }

    this.actionSelected.emit({
      itemId: this.row().id,
      kind: action.kind,
    });
  }
}
